'use client';
import { useRef, useState } from 'react';
import Image from 'next/image';
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { motion, useScroll, useTransform } from 'motion/react';
import { ArrowUpRight, ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import { ICONS } from '@/lib/iconRegistry';
import { Carousel, CarouselContent, CarouselItem } from '@ui/carousel';


function PillarCard({ pillar, index, scrollYProgress, isMobile }) {
    const Icon = ICONS[pillar.icon];
    const isEven = index % 2 === 1;
    const start = 0.05 + index * 0.22;
    const end = start + 0.28;
    const y = useTransform(scrollYProgress, [start, end], [80, 10]);
    const opacity = useTransform(scrollYProgress, [start, end], [0, 1.5]);
    const motionStyle = isMobile ? undefined : { y, opacity };

    const labelSide = (
        <div className="relative flex flex-col justify-between p-10 md:p-12 overflow-hidden shrink-0 w-full md:w-[38%]">
            {pillar.image ? (
                <Image
                    src={pillar.image}
                    alt={pillar.title}
                    fill
                    sizes="(max-width: 768px) 100vw, 40vw"
                    draggable="false"
                    quality={80}
                    className="object-cover object-center"
                    aria-hidden
                />
            ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-gray-800/60" aria-hidden />
            )}
            <div className={`absolute inset-0 ${isEven ? 'bg-primary/85' : 'bg-gray-900/90'}`} />
            <div className='flex flex-col gap-5'>
                <div className="relative z-10 mt-16">
                    <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-5">
                        {Icon && <Icon size={22} className="text-white" />}
                    </div>
                    <span className="text-2xl md:text-3xl font-bold text-white leading-snug">{pillar.subTitle}</span>
                </div>
                {pillar.linkUrl && (
                    <Link href={`${pillar.linkUrl}`} className='w-fit'>
                        <Button size="lg" className="bg-red-gradient border-0 text-sm md:text-base py-5 px-4 btn-glass group cursor-pointer">
                            <span className="relative z-10 text-sm flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400 leading-snug">
                                {pillar.buttonText}
                                <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                            </span>
                        </Button>
                    </Link>
                )}
            </div>

            {pillar.cardContent && (
                <div className="relative z-10 mt-8">
                    <div className="relative flex gap-3 p-5 rounded-2xl bg-black/30 backdrop-blur-md border border-white/15 overflow-hidden">
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-gradient" aria-hidden />
                        <p className="pl-2 text-white text-sm leading-relaxed font-medium">
                            {pillar.cardContent}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );

    const contentSide = (
        <div className="flex-1 bg-[#f7f8fc] p-10 md:p-12 flex flex-col justify-start md:justify-center space-y-5">
            <h3 className="text-2xl md:text-3xl font-bold">{pillar.title}</h3>
            {pillar.body && (Array.isArray(pillar.body)
                ? pillar.body.map((line, i) => <p key={i} className="text-muted-foreground mb-2">{line}</p>)
                : <p className="text-muted-foreground">{pillar.body}</p>
            )}

            {(pillar.values || pillar.steps) && (
                <div className={pillar.values && pillar.steps ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : ''}>
                    {pillar.values && (
                        <div>
                            {pillar.valuesIntro && (
                                <p className="text-gray-900 font-semibold mb-2">{pillar.valuesIntro}</p>
                            )}
                            <ul className="space-y-4">
                                {pillar.values.map((v, i) => (
                                    <li key={i} className="flex gap-3 mb-2">
                                        <span className="mt-1 flex-shrink-0 w-4 h-4 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                                            <Check size={10} className="text-primary" />
                                        </span>
                                        <p className="text-muted-foreground">
                                            {v.term &&
                                                <span className="text-gray-900 font-semibold">{v.term}
                                                    {v.desc && <span>:</span>}
                                                </span>}
                                            {v.term && v.desc && ' '}
                                            {v.desc}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {pillar.steps && (
                        <div>
                            {pillar.stepsIntro && (
                                <p className="text-gray-900 font-semibold mb-2">{pillar.stepsIntro}</p>
                            )}
                            <ol className="space-y-4">
                                {pillar.steps.map((s, i) => (
                                    <li key={i} className="flex gap-3 mb-2">
                                        <span className="mt-1 flex-shrink-0 w-4 h-4 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                                            <X size={10} className="text-primary" />
                                        </span>
                                        <p className="text-muted-foreground">{s}</p>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                </div>
            )}

            {pillar.table && (
                <div className="overflow-x-auto rounded-xl border border-primary/10">
                    <table className="w-full text-sm text-left border-collapse">
                        <thead>
                            <tr className="bg-primary/5">
                                {pillar.table.headers.map((h, i) => (
                                    <th key={i} className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pillar.table.rows.map((row, i) => (
                                <tr key={i} className={i % 2 === 1 ? 'bg-white' : 'bg-primary/[0.02]'}>
                                    {row.map((cell, j) => (
                                        <td
                                            key={j}
                                            className={`px-4 py-3 align-top border-t border-primary/10 ${j === 0 ? 'font-bold text-gray-900' : 'text-muted-foreground'}`}
                                        >
                                            {cell}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {pillar.footerContent && (
                <p className="text-center text-muted-foreground mt-4 text-sm md:text-base">
                    {pillar.footerContent.text}{" "}
                    <Link
                        href={pillar.footerContent.linkHref}
                        className="text-primary font-semibold underline underline-offset-4 hover:text-primary/80 transition-colors"
                    >
                        {pillar.footerContent.linkLabel}
                    </Link>
                    {" "}{pillar.footerContent.suffix}
                </p>
            )}

            {pillar.metrics && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {pillar.metrics.map((m, i) => (
                        <div key={i} className="p-4 rounded-xl bg-white border border-primary/10 text-center shadow-sm">
                            <p className="text-3xl font-black text-primary leading-none">
                                {m.value}
                                {m.unit && <span className="text-sm ml-1 font-semibold">{m.unit}</span>}
                            </p>
                            <p className="text-sm font-semibold text-gray-800 mt-2">{m.label}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{m.sub}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    if (pillar.table && !pillar.withImage) {
        return (
            <motion.div
                style={motionStyle}
                className="overflow-hidden rounded-2xl shadow-md"
            >
                {contentSide}
            </motion.div>
        );
    }

    return (
        <motion.div
            style={motionStyle}
            className={`flex flex-col md:flex-row overflow-hidden rounded-2xl shadow-md h-full md:h-auto min-h-[260px] ${isEven ? 'md:flex-row-reverse' : ''}`}
        >
            {labelSide}
            {contentSide}
        </motion.div>
    );
}

export default function ContentSection({ data = [] }) {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start 90%', 'end 80%'],
    });

    const [api, setApi] = useState(null);

    return (
        <section ref={sectionRef} className="relative bg-white pt-16 pb-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-[700px] h-[500px] bg-primary/5 rounded-full blur-[140px] pointer-events-none" />
            <div className="max-w-[1440px] mx-auto px-6">
                {/* Mobile: swipeable slider with arrow navigation */}
                <div className="md:hidden">
                    <Carousel setApi={setApi} opts={{ loop: true, align: 'start' }} className="w-full">
                        <CarouselContent>
                            {data.map((p, i) => (
                                <CarouselItem key={p.number} className="basis-full">
                                    <PillarCard
                                        pillar={p}
                                        index={i}
                                        scrollYProgress={scrollYProgress}
                                        isMobile
                                    />
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    </Carousel>

                    <div className="flex items-center justify-center gap-5 mt-6">
                        <button
                            onClick={() => api?.scrollPrev()}
                            aria-label="Previous"
                            className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors duration-200 cursor-pointer"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            onClick={() => api?.scrollNext()}
                            aria-label="Next"
                            className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors duration-200 cursor-pointer"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>

                {/* Desktop: full stacked list */}
                <div className="hidden md:block space-y-5">
                    {data.map((p, i) => (
                        <PillarCard
                            key={p.number}
                            pillar={p}
                            index={i}
                            scrollYProgress={scrollYProgress}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
